/**
 * TopMovers - Top gainers and losers from the portfolio.
 *
 * Two-column grid: top 3 gainers (left), top 3 losers (right).
 * Each shows symbol, name, % change, and an Analyze link.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type { Position } from '@/types/portfolio';

interface TopMoversProps {
  positions: Position[];
}

function MoverCard({ pos, rank }: { pos: Position; rank: number }) {
  const navigate = useNavigate();
  const isPositive = pos.total_gain_loss_pct >= 0;

  return (
    <button
      onClick={() => navigate(`/dashboard/stock-analysis?ticker=${pos.symbol}`)}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-light/50 transition-colors text-left w-full group"
    >
      <span className="text-xs text-text-muted w-4 text-center">{rank}</span>
      <TickerLogo symbol={pos.symbol} name={pos.name} size="xs" />
      <div className="flex-1 min-w-0">
        <span className="font-mono text-sm font-semibold text-text-primary">{pos.symbol}</span>
        {pos.name && (
          <p className="text-xs text-text-muted truncate">{pos.name}</p>
        )}
      </div>
      <span className={cn(
        'text-sm font-semibold tabular-nums shrink-0 whitespace-nowrap',
        isPositive ? 'text-green-400' : 'text-red-400'
      )}>
        {isPositive ? '+' : ''}{pos.total_gain_loss_pct.toFixed(1)}%
      </span>
    </button>
  );
}

export function TopMovers({ positions }: TopMoversProps) {
  const { gainers, losers } = useMemo(() => {
    const sorted = [...positions].sort((a, b) => b.total_gain_loss_pct - a.total_gain_loss_pct);
    return {
      gainers: sorted.filter(p => p.total_gain_loss_pct > 0).slice(0, 3),
      losers: sorted.filter(p => p.total_gain_loss_pct < 0).slice(-3).reverse(),
    };
  }, [positions]);

  if (gainers.length === 0 && losers.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Gainers */}
      <div className="rounded-xl border border-border bg-surface-light/30 p-3">
        <div className="flex items-center gap-2 px-3 pb-2 border-b border-border mb-1">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">Top Gainers</span>
        </div>
        {gainers.length > 0 ? (
          gainers.map((pos, i) => <MoverCard key={pos.symbol} pos={pos} rank={i + 1} />)
        ) : (
          <p className="text-xs text-text-muted text-center py-4">No gainers</p>
        )}
      </div>

      {/* Losers */}
      <div className="rounded-xl border border-border bg-surface-light/30 p-3">
        <div className="flex items-center gap-2 px-3 pb-2 border-b border-border mb-1">
          <TrendingDown className="w-4 h-4 text-red-400" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">Top Losers</span>
        </div>
        {losers.length > 0 ? (
          losers.map((pos, i) => <MoverCard key={pos.symbol} pos={pos} rank={i + 1} />)
        ) : (
          <p className="text-xs text-text-muted text-center py-4">No losers</p>
        )}
      </div>
    </div>
  );
}
